import React from "react";
import { MoreVertical } from "lucide-react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface ActionItem {
  key: string;
  render: () => React.ReactNode;
  onClick: () => void;
}

interface HeaderActionsProps {
  actions: ActionItem[];
  disabled?: boolean;
  size?: "sm" | "default";
}

export function HeaderActions({
  actions,
  disabled = false,
  size = "default",
}: HeaderActionsProps) {
  const { t } = useTranslation();

  return (
    <Menubar
      role="menubar"
      aria-label={t("studentList.actions.menubarLabel")}
      className={cn(
        "p-0",
        size === "sm" && "h-8",
        disabled
          ? "pointer-events-none cursor-not-allowed text-primary-extra-light opacity-50"
          : "cursor-pointer hover:bg-muted",
      )}
    >
      <MenubarMenu>
        <MenubarTrigger
          role="menuitem"
          aria-label={t("studentList.actions.openMenu")}
          aria-haspopup="true"
          aria-expanded="false"
          aria-disabled={disabled}
          className={cn(size === "sm" && "h-8 w-8")}
        >
          <MoreVertical aria-hidden="true" />
        </MenubarTrigger>
        <MenubarContent
          align="end"
          role="menu"
          data-testid="student-list-actions-menu"
          aria-label={t("studentList.actions.menu")}
          aria-orientation="vertical"
        >
          {actions.map((action) => (
            <React.Fragment key={action.key}>
              <MenubarItem
                onClick={action.onClick}
                className={cn("flex cursor-pointer items-center")}
                role="menuitem"
                aria-label={action.key}
                tabIndex={0}
              >
                {action.render()}
              </MenubarItem>
            </React.Fragment>
          ))}
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
}
